import React from 'react';
import styles from "../../OfferForm.module.css";
import {OfferFormSelectionProvider, OfferRequest, OfferRequestPartial} from "../../OfferForm";
import {ValidationResult} from "../../../../../../elements/validator/component/ValidationComponent";
import {
    FloatNumberValidator,
    NotEmptyStringValidator,
    PercentageValidator
} from "../../../../../../elements/validator/Validator";
import {ValidatedInputField} from "../../../../../../elements/validator/component/ValidatedInputField";
import {selectCurrency} from "../../../../../assistant/page/toolSettings/toolSettingsSlice";
import {ValidatedSelectField} from "../../../../../../elements/validator/component/ValidatedSelectField";
import {Currency} from "../../../../../../../api/generated";
import {allCurrencyOptions} from "../../../../../assistant/modal/createAndUpdate/components/util/currencyUtil";

interface Props<T extends OfferRequest> {
    getMessage: (s: any) => any;
    updateRequest: (update: OfferRequestPartial) => void;
    selectionProvider: OfferFormSelectionProvider;
    updateValidationStatus: (status: ValidationResult<T>) => void,
}

export const OfferWithFinanceInfoFields = <T extends OfferRequest>({
                                                                       getMessage,
                                                                       updateRequest,
                                                                       selectionProvider,
                                                                       updateValidationStatus
                                                                   }: Props<T>) => {

    return <>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<string, T>
                required={true}
                onChange={(v) => updateRequest({financeProduct: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'financeProduct'}
                selector={selectionProvider.financeProduct}
                validator={NotEmptyStringValidator}
                label={'Finance product'}
                getMessage={getMessage}
            />
            <ValidatedSelectField<Currency, T>
                required={true}
                onChange={(v) => updateRequest({currency: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'currency'}
                selector={selectCurrency}
                validator={NotEmptyStringValidator}
                label={'Currency'}
                getMessage={getMessage}
                options={allCurrencyOptions}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({cashPrice: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'cashPrice'}
                selector={selectionProvider.cashPrice}
                validator={FloatNumberValidator}
                label={'Cash price'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({monthlyPayments: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'monthlyPayments'}
                selector={selectionProvider.monthlyPayments}
                validator={FloatNumberValidator}
                label={'Monthly payments'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({customerDeposit: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'customerDeposit'}
                selector={selectionProvider.customerDeposit}
                validator={FloatNumberValidator}
                label={'Customer deposit'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({manufacturerDepositContribution: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'manufacturerDepositContribution'}
                selector={selectionProvider.manufacturerDepositContribution}
                validator={FloatNumberValidator}
                label={'Manufacturer deposit contribution'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({totalDeposit: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'totalDeposit'}
                selector={selectionProvider.totalDeposit}
                validator={FloatNumberValidator}
                label={'Total deposit'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({dealerContribution: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'dealerContribution'}
                selector={selectionProvider.dealerContribution}
                validator={FloatNumberValidator}
                label={'dealer contribution'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({balanceToFinance: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'balanceToFinance'}
                selector={selectionProvider.balanceToFinance}
                validator={FloatNumberValidator}
                label={'Balance to finance'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({totalAmountPayable: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'totalAmountPayable'}
                selector={selectionProvider.totalAmountPayable}
                validator={FloatNumberValidator}
                label={'Total amount payable'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({totalChargeForCredit: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'totalChargeForCredit'}
                selector={selectionProvider.totalChargeForCredit}
                validator={FloatNumberValidator}
                label={'Total charge for credit'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({optionalFinalPayment: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'optionalFinalPayment'}
                selector={selectionProvider.optionalFinalPayment}
                validator={FloatNumberValidator}
                label={'Optional final payment'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({durationOfAgreementInMonths: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'durationOfAgreementInMonths'}
                selector={selectionProvider.durationOfAgreementInMonths}
                validator={FloatNumberValidator}
                label={'Duration of agreement in months'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({apr: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'apr'}
                selector={selectionProvider.apr}
                validator={PercentageValidator}
                label={'APR'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({fixedInterestRate: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'fixedInterestRate'}
                selector={selectionProvider.fixedInterestRate}
                validator={PercentageValidator}
                label={'Fixed interest rate'}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({mileagePerAnnum: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'mileagePerAnnum'}
                selector={selectionProvider.mileagePerAnnum}
                validator={FloatNumberValidator}
                label={'Mileage per annum'}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={true}
                onChange={(v) => updateRequest({excessMileageCharge: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'excessMileageCharge'}
                selector={selectionProvider.excessMileageCharge}
                validator={FloatNumberValidator}
                label={'Excess mileage charge'}
                getMessage={getMessage}
            />
        </div>
    </>
}
